import { useContext, useState } from "react"
import { 
  NOTIFICATION_TYPE_ID, 
  NOTIFICATION_STATUS_ID, 
  NOTIFICATION_RECIPIENT_TYPE_ID 
} from "@constants"

import { NotificationContext } from "@context"
import { CREATE_NOTIFICATION_MUTATION, GET_SES_EMAIL_BATCHES } from "@graphql"
import { addNotificationToCache, getGraphQLErrorMessage } from "@helpers"
import { useInvitationsLoader } from "@hooks"

import { 
  AdjustmentsHorizontalIcon, 
  PaperAirplaneIcon, 
  UsersIcon 
} from "@heroicons/react/24/outline"

import { useMutation, useLazyQuery } from "@apollo/client"
import { Step1, Step2, Step3 } from "./Steps"
import PropTypes from "prop-types"

const filters = [
  { value: "INVITATION_EMAIL_NEVER_SENT", caption: "Outstanding Invitations", description: "Email all guardians who have yet to receive an invitation email." },
  { value: "ALL", caption: "All", description: "Email all guardians (including those who have already been sent an invitation)." },
  { value: "UNACTIVATED", caption: "Account not activated", description: "Email guardians who have yet to activate their account. This can be used as a reminder email." },
  { value: "NO_BOOKINGS", caption: "Guardian made no bookings", description: "Email guardians who have not yet made any bookings. This can be used as a reminder email." },
]

const steps = [
  { stepNo: 1, title: "Choose Recipients", "description": "Use filters to quickly find the right guardians to email, or start with a full list and narrow it down later.", icon: <UsersIcon className="w-8 h-8" />, isSubmitting: false },
  { stepNo: 2, title: "Filter Guardians", "description": "Browse the filtered list and select or deselect individual guardians before sending.", icon: <AdjustmentsHorizontalIcon className="w-8 h-8" />, isSubmitting: false },
  { stepNo: 3, title: "Confirm & Send", "description": "Double-check your selection and send the emails.", icon: <PaperAirplaneIcon className="w-8 h-8" />, isSubmitting: false },
]

const InvitationEmails = ({ event, handleClose, handleRefetchMatrix, handleSetIsSending }) => {
  const [currentStep, setCurrentStep] = useState(steps[0])
  const [isComplete, setIsComplete] = useState(false)
  const {notify} = useContext(NotificationContext)

  const {
    invitations,
    invitationSelections,
    filter,
    searchTerm,
    page,
    setInvitationSelections,
    loadInvitations
  } = useInvitationsLoader({ eventId: event.id, notify })

  const [getEmailOutstandingEmailBatches] = useLazyQuery(GET_SES_EMAIL_BATCHES, {
    fetchPolicy: "no-cache",
    variables: { 
      input: { 
        event_id: event?.id, 
        outstanding_only: true,
        types: ["INVITATION"]
      } 
    },

    onError: (errors) => {
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while fetching outstanding email batches."), open: true })
    },
  })

  const [createNotification] = useMutation(CREATE_NOTIFICATION_MUTATION, {
    onCompleted: (data) => {
      setIsComplete(true)
      handleSetIsSending(true);
    },
     
    onError: (errors) => {
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while creating the notification."), open: true })
      setCurrentStep(prevStep => ({ ...prevStep, isSubmitting: false }))
    },

    update: (cache, { data: { createNotification } }) => addNotificationToCache(cache, createNotification, event.id)
  })

  const getSelectedInvitations = () => invitationSelections.filter((invitation) => invitation.selected)
     
  const handleLoadMore = async () => {
    loadInvitations(null, null, [], page+1, true, true)
  }

  const handleStep1 = async (filter) => {
    setCurrentStep(prevStep => ({ ...prevStep, isSubmitting: true }))
    await loadInvitations(filter, null, [], 1)
    setCurrentStep(steps[1])
  }

  const handleStep2 = () => {
    setCurrentStep(steps[2])
  }

  const handleSearchChange = (searchTerm) => {
    loadInvitations(null, searchTerm, [], 1)
  }
  
  const handleSelectAllInvitations = () => {
    const newInvitationSelections = invitationSelections.map((selection) => ({ ...selection, selected: true }))
    setInvitationSelections(newInvitationSelections)
  }

  const handleSendInvitations = () => {
    const selectedInvitations = getSelectedInvitations()
    
    setCurrentStep(prevStep => ({ ...prevStep, isSubmitting: true }))
  
    getEmailOutstandingEmailBatches().then((response) => {
      const totalQueued = response?.data?.sesEmailBatches.reduce((sum, batch) => sum + batch.no_of_queued, 0)

      if (totalQueued > 0) {
        notify({ title: "Sending Emails", type: "info", content: "There are emails currently being sent in the background. Please wait until they have completed before sending more invitations.", open: true })
        setCurrentStep(prevStep => ({ ...prevStep, isSubmitting: false }))
        return
      }

      const title = selectedInvitations.length > 1 
        ? "Multiple Guardians" 
        : `${invitations.find(invitation => invitation.id === selectedInvitations[0].invitation_id).guardian}`

      createNotification({
        variables: {
          input: {
            event_id: event.id,
            title: title,
            description: "Invitation email sent to selected guardians.",
            type_id: NOTIFICATION_TYPE_ID,
            status_id: NOTIFICATION_STATUS_ID,
            recipient_type_id: NOTIFICATION_RECIPIENT_TYPE_ID,
            user_ids: selectedInvitations.map((invitation) => invitation.user_id),
          },
        },
      }).finally(() => {
        setCurrentStep(prevStep => ({ ...prevStep, isSubmitting: false }));
      });
    })
  }

  const handleClearAllInvitations = () => {
    const newInvitationSelections = invitationSelections.map((selection) => ({ ...selection, selected: false }))
    setInvitationSelections(newInvitationSelections)
  }

  const handleReturnToMatrix = () => {
    handleRefetchMatrix()
    handleClose()
  }

  const handleToggleSelectedInvitation = (invitationId, value) => {
    setInvitationSelections(prev => prev.map(invitationSelection => {
      return invitationSelection.invitation_id === invitationId ? { ...invitationSelection, selected: value } : invitationSelection
    }))
  }

  const selectedFilter = filters.find((filterOption) => filterOption.value === filter)
  
  const counts = {
    selected: invitationSelections.filter((selection) => selection.selected).length,
    deselected: invitationSelections.filter((selection) => !selection.selected).length,
    total: invitationSelections.length,
  }
  
  return (
    <div>      
      {currentStep.stepNo === 1 && 
        <Step1 
          filter={filter} 
          filters={filters}
          handlePrevious={() => handleClose()} 
          handleNext={(filter) => handleStep1(filter)}
          isSubmitting={currentStep.isSubmitting} 
        />
      }

      {currentStep.stepNo === 2 && 
        <Step2 
          invitations={invitations}
          handlePrevious={() => setCurrentStep(steps[0])} 
          handleNext={() => handleStep2()}
          handleSearchChange={(searchTerm) => handleSearchChange(searchTerm)}
          handleSearchClear={() => handleSearchChange("")}
          handleClearAll={() => handleClearAllInvitations()}
          handleSelectAll={() => handleSelectAllInvitations()}
          handleToggleSelected={(invitationId, value) => handleToggleSelectedInvitation(invitationId, value)}
          handleLoadMore={() => handleLoadMore()}
          searchTerm={searchTerm}
          invitationSelections={invitationSelections}
          isSubmitting={currentStep.isSubmitting}
        />
      }

      {currentStep.stepNo === 3 && 
        <Step3
          filter={selectedFilter}
          handlePrevious={() => setCurrentStep(steps[1])} 
          handleFinish={() => isComplete ? handleReturnToMatrix() : handleSendInvitations()}
          counts={counts}
          isComplete={isComplete}
          isSubmitting={currentStep.isSubmitting}
        />
      }
    </div>
  )
}

InvitationEmails.propTypes = {
  event: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleRefetchMatrix: PropTypes.func.isRequired,
  handleSetIsSending: PropTypes.func.isRequired,
}

export default InvitationEmails